import { AxiosResponse } from 'axios';
import useRefetch from 'hooks/useRefetch';
import useUndoActions from 'hooks/useUndoActions';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useCallback, useContext, useEffect, useState } from 'react';

type UseRevokeFileAccessProps<T> = {
  numberOfAccess: number;
  value: T;
  handleOnRevokeAccess: () => Promise<AxiosResponse>;
  handleOnUndoRevokeAccess: () => Promise<AxiosResponse>;
  // Resolves false to keep the access.
  confirmRevokeAccess?: () => Promise<boolean>;
  updateValueOnRevokeSuccess?: (value: T) => void;
  updateValueOnUndoSuccess?: (value: T) => void;
};

export default function useRevokeFileAccess<T>({
  value,
  numberOfAccess,
  handleOnRevokeAccess,
  handleOnUndoRevokeAccess,
  confirmRevokeAccess,
  updateValueOnRevokeSuccess,
  updateValueOnUndoSuccess,
}: UseRevokeFileAccessProps<T>) {
  const [isRevokedAccess, setIsRevokedAccess] = useState(false);
  const [isLoadingRevoke, setIsLoadingRevoke] = useState(false);

  const [prevState, setPrevState] = useState(null);

  const { refetchFlag, refetch } = useRefetch();

  const hasFileAccess = !isRevokedAccess && numberOfAccess > 0;

  const { isUndoAvailable, clearUndoTimeout, setUndoTimeout } =
    useUndoActions();

  const { popServerError } = useContext(PopNotificationsContext);

  const onRevokeSuccess = useCallback(() => {
    setPrevState(value);
    updateValueOnRevokeSuccess(value);
  }, [value, updateValueOnRevokeSuccess]);

  const onUndoSuccess = useCallback(() => {
    refetch();
    updateValueOnUndoSuccess(prevState);
  }, [prevState, refetch, updateValueOnUndoSuccess]);

  const onRevokeAccess = useCallback(async () => {
    try {
      setIsLoadingRevoke(true);

      if (confirmRevokeAccess && !(await confirmRevokeAccess())) return;

      await handleOnRevokeAccess();

      setIsRevokedAccess(true);
      onRevokeSuccess?.();

      setUndoTimeout(() => setTimeout(refetch, 500));
    } catch (error) {
      popServerError(error);
    } finally {
      setIsLoadingRevoke(false);
    }
  }, [
    confirmRevokeAccess,
    handleOnRevokeAccess,
    onRevokeSuccess,
    popServerError,
    refetch,
    setUndoTimeout,
  ]);

  const onUndoRevokeAccess = useCallback(async () => {
    try {
      setIsLoadingRevoke(true);
      await handleOnUndoRevokeAccess();

      setIsRevokedAccess(false);
      clearUndoTimeout();
      onUndoSuccess?.();
    } catch (error) {
      popServerError(error);
    } finally {
      setIsLoadingRevoke(false);
    }
  }, [
    handleOnUndoRevokeAccess,
    onUndoSuccess,
    popServerError,
    clearUndoTimeout,
  ]);

  useEffect(() => {
    setIsRevokedAccess(false);
  }, [numberOfAccess]);

  return {
    hasFileAccess,
    isUndoAvailable,
    isLoadingRevoke,
    refetchFlag,
    onRevokeAccess,
    onUndoRevokeAccess,
    refetch,
  };
}
